if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
        this.onDuplicateQuickviewControlClick = this.onDuplicateQuickviewControlClick.bind(this);
        this.onSliderBreakpoint = this.onSliderBreakpoint.bind(this);
      }

      connectedCallback() {
        this.removeEventListener('click', this.onDuplicateQuickviewControlClick);
        this.addEventListener('click', this.onDuplicateQuickviewControlClick);

        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
        };

        this.sectionId = this.dataset.sectionId;
        this.section = this.closest(`.section-${this.sectionId}`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.items = parseInt(this.dataset.items, 10);
        this.tabletItems = parseInt(this.dataset.tabletItems, 10);

        this.sliderInstance = false;

        if (!this.enableSlider) return;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);

        if (mql.matches) {
          this.destroySlider();
          if (!(window.Shopify && Shopify.designMode)) return;
        }

        const start = () => {
          if (!window.FoxTheme || !window.FoxTheme.Carousel) {
            requestAnimationFrame(start);
            return;
          }
          this.waitForStableLayout(() => this.init());
        };
        if (window.Shopify && Shopify.designMode) {
          start();
          return;
        }

        if ('IntersectionObserver' in window) {
          const io = new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              io.disconnect();
              start();
            },
            { rootMargin: '200px 0px' }
          );
          io.observe(this);
        } else {
          start();
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onDuplicateQuickviewControlClick);
        this.disconnectLayoutObserver();
      }

      init() {
        if (FoxTheme.config.mqlMobile) {
          this.destroySlider();
        } else {
          this.initSlider();
        }
      }

      /**
       * 刷新时浏览器会先按顶部排版再恢复滚动，异步 CSS / 进场动画也会改宽度。
       * 连续两帧宽度不变后再初始化，避免 Swiper loop 用到半成品尺寸。
       */
      waitForStableLayout(callback) {
        const startedAt = performance.now();
        const maxWait = 1800;
        let lastWidth = this.clientWidth;
        let stableFrames = 0;

        const tick = () => {
          const width = this.clientWidth;
          if (width > 0 && width === lastWidth) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
            lastWidth = width;
          }

          if (stableFrames >= 2 || performance.now() - startedAt > maxWait) {
            callback();
            return;
          }
          requestAnimationFrame(tick);
        };

        requestAnimationFrame(tick);
      }

      /**
       * 刷新时如果 section 已经在视口里，grid-list 会先把卡片 visibility:hidden。
       * 这时初始化 loop 会量到错误位移，懒加载图也不会开始请求。
       * 从顶部滚下来时 section 还在视口外，这里直接返回，保留浮现动画。
       */
      revealSlidesIfAlreadyVisible() {
        const rect = this.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) return;

        this.querySelectorAll('.f-column').forEach((el) => {
          if (typeof el.getAnimations === 'function') {
            el.getAnimations().forEach((animation) => animation.cancel());
          }
          el.style.visibility = 'visible';
          el.style.opacity = '1';
          el.style.transform = 'none';
        });

        this.querySelectorAll('.product-card__image--main img[loading="lazy"]').forEach((img) => {
          if (!img.complete) img.loading = 'eager';
        });
      }

      initSlider() {
        if (typeof this.sliderInstance === 'object') return;
        if (!this.sliderWrapper) return;
        if (this.clientWidth < 1) return;

        this.revealSlidesIfAlreadyVisible();

        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--column-gap');
        const spaceBetween = parseFloat(columnGap.replace('rem', '')) * 10;
        const desktopPerView = parseInt(this.items, 10) || 1;
        const tabletPerView = this.tabletItems > 3 ? 3 : parseInt(this.tabletItems, 10) || 1;

        this.sliderOptions = {
          slidesPerView: tabletPerView,
          spaceBetween: Number.isFinite(spaceBetween) ? spaceBetween : 0,
          navigation: {
            nextEl: this.section.querySelector(this.selectors.nextEl),
            prevEl: this.section.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            1280: {
              slidesPerView: desktopPerView,
            },
          },
          loop: true,
          threshold: 2,
        };

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions);
        this.sliderInstance.init();

        this.fixQuickviewDuplicate();
        this.sliderInstance.slider?.on('breakpoint', this.onSliderBreakpoint);
        this.observeSliderLayout();

        requestAnimationFrame(() => {
          this.relayoutSlider();
        });
      }

      relayoutSlider() {
        const swiper = this.sliderInstance && this.sliderInstance.slider;
        if (!swiper || swiper.destroyed) return;

        if (typeof swiper.emit === 'function') {
          swiper.emit('resize');
        }
        if (swiper.params.loop && typeof swiper.loopFix === 'function') {
          swiper.loopFix();
        }

        this.calcNavButtonsPosition();
        this.calcHeadingColumnHeight();
      }

      observeSliderLayout() {
        this.disconnectLayoutObserver();
        if (typeof ResizeObserver !== 'function') return;

        let lastWidth = this.clientWidth;
        this._layoutObserver = new ResizeObserver(() => {
          const width = this.clientWidth;
          if (width < 1 || width === lastWidth) return;
          lastWidth = width;
          clearTimeout(this._layoutTimer);
          this._layoutTimer = setTimeout(() => this.relayoutSlider(), 50);
        });
        this._layoutObserver.observe(this);
      }

      disconnectLayoutObserver() {
        clearTimeout(this._layoutTimer);
        if (this._layoutObserver) {
          this._layoutObserver.disconnect();
          this._layoutObserver = null;
        }
      }

      destroySlider() {
        this.disconnectLayoutObserver();
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty(
            '--swiper-navigation-top-offset',
            parseInt(firstMedia.clientHeight, 10) / 2 + 'px'
          );
        }
      }

      calcHeadingColumnHeight() {
        if (this.dataset.calcHeadingColumnHeight !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.section.style.setProperty('--heading-column-height', firstMedia.clientHeight + 'px');
        }
      }

      onSliderBreakpoint() {
        requestAnimationFrame(() => {
          if (this.isConnected) this.fixQuickviewDuplicate();
        });
      }

      onDuplicateQuickviewControlClick(event) {
        const control = event.target.closest?.('[aria-controls]');
        if (!control || !control.closest('.swiper-slide-duplicate')) return;

        const modalId = control.getAttribute('aria-controls');
        const modal = modalId ? document.getElementById(modalId) : null;
        if (!modal || modal.tagName.toLowerCase() !== 'quick-view-modal') return;

        event.preventDefault();
        event.stopPropagation();
        if (modal.open) {
          modal.hide();
        } else {
          modal.show(control);
        }
      }

      fixQuickviewDuplicate() {
        const seenModalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const slide = modal.closest('.swiper-slide');
          if (slide && slide.classList.contains('swiper-slide-duplicate')) {
            modal.remove();
            return;
          }

          const modalID = modal.getAttribute('id');
          if (!modalID || seenModalIds.includes(modalID)) {
            modal.remove();
            return;
          }
          seenModalIds.push(modalID);
        });
      }
    }
  );
}
